import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Select,
  Stack,
  Textarea,
  TextInput,
  Button,
  Group,
  ActionIcon,
  Table,
  LoadingOverlay,
  Text,
  TagsInput
} from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterQuestionService } from 'services/lia/masterQuestionService';
import { UpdateMasterQuestionDto, RangeItem } from 'services/api';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import RangeTemplateSelector from 'components/RangeTemplateSelector';

const questionsPath = '/app/lia/questions/master-questions/list';
const groupSelectionPath = '/app/lia/questions/master-questions';

const buildGroupReturnPath = (requestedPath: string | undefined, groupId: string) => {
  const [pathname, query = ''] = requestedPath?.split('?') || [];
  const params = new URLSearchParams(pathname === questionsPath ? query : '');
  params.delete('groups');
  params.set('group', groupId);
  return `${questionsPath}?${params.toString()}`;
};

// Schema for range items
const rangeItemSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  value: z.string().min(1, 'Value is required'),
  sequence: z.number().optional(),
  msQuestionRangeItemID: z.string().optional()
});

const questionSchema = z.object({
  MsQuestionID: z.string(),
  Category: z.array(z.string()).default([]),
  Title: z.string().min(1, 'Question is required'),
  Description: z.string().optional(),
  Type: z.enum(['Choose One', 'Range', 'Free-text']),
  MsGroupQuestionID: z.string().min(1, 'Group Question is required'),
  Sequence: z.number().default(1),
  RangeItem: z.array(rangeItemSchema).optional().default([]),
  SkalaCategory: z.string().optional()
});

type FormData = z.infer<typeof questionSchema>;

const EditQuestionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get('id');
  const requestedGroupId = searchParams.get('group') || '';
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      MsQuestionID: questionId || '',
      RangeItem: [],
      Category: [],
      Description: ''
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'RangeItem'
  });

  const questionType = watch('Type');

  const { data: questionData, isLoading: isLoadingQuestion } = useQuery({
    queryKey: ['question', questionId],
    queryFn: async () => {
      if (!questionId) return null;
      return await masterQuestionService.getDetail(questionId);
    },
    enabled: !!questionId,
    retry: 1
  });

  const requestedReturnPath = (location.state as { returnTo?: string } | null)?.returnTo;
  const question = questionData?.data || questionData;
  const questionGroupId = question?.msGroupQuestionID || requestedGroupId;
  const returnPath = questionGroupId
    ? buildGroupReturnPath(requestedReturnPath, questionGroupId)
    : groupSelectionPath;
  const items = [
    { title: 'Questions', href: returnPath },
    { title: 'Edit Question', href: '' }
  ];

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => {
      const rangeItems: RangeItem[] =
        data.RangeItem?.map((item, index) => ({
          label: item.label,
          value: item.value,
          sequence: item.sequence || index + 1,
          msQuestionRangeItemID: item.msQuestionRangeItemID
        })) || [];

      const updateDto: UpdateMasterQuestionDto = {
        MsQuestionID: data.MsQuestionID,
        Category: data.Category.join(', '),
        Title: data.Title,
        Description: data.Description,
        SkalaCategory: data.SkalaCategory,
        Type: data.Type,
        MsGroupQuestionID: data.MsGroupQuestionID,
        Sequence: data.Sequence,
        RangeItem: data.Type === 'Range' ? rangeItems : undefined
      };

      return masterQuestionService.update(data.MsQuestionID, updateDto);
    },
    onSuccess: () => {
      navigate(returnPath);
      showSuccessNotification({
        title: 'Updated Successfully!',
        message: 'Question has been updated successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to update question',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  useEffect(() => {
    if (questionData) {
      reset({
        MsQuestionID: question.msQuestionID,
        Category: question.category
          ? question.category.split(',').map((cat: string) => cat.trim())
          : [],
        Title: question.title,
        Description: question.description ?? '',
        Type: question.type,
        MsGroupQuestionID: question.msGroupQuestionID,
        Sequence: question.sequence || 1,
        SkalaCategory: question.skalaCategory || '',
        RangeItem: []
      });

      if (question.type === 'Range' && question.rangeItem?.length) {
        replace(
          question.rangeItem.map((item: any) => ({
            label: item.label,
            value: item.value,
            sequence: item.sequence || 1,
            msQuestionRangeItemID: item.msQuestionRangeItemID
          }))
        );
      }
    }
  }, [questionData, reset, replace]);

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processData = async () => {
    if (formData) {
      updateMutation.mutate(formData);
    }
    close();
  };

  if (!questionId) {
    navigate(returnPath);
    return null;
  }

  const isLoading = isLoadingQuestion;

  const handleTemplateSelect = (templateItems: any[]) => {
    if (fields.length > 0) {
      remove();
    }

    if (templateItems && templateItems.length > 0) {
      templateItems.forEach((item: any) => {
        append({
          label: item.label,
          value: item.value,
          sequence: item.sequence || 1
        });
      });
    }
  };

  return (
    <>
      <PageTitle title='Edit Question' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Update'
        statusProcess='edit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processData}
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={isLoading || updateMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap='md'>
            <Controller
              name='Title'
              control={control}
              render={({ field }) => (
                <Textarea
                  autosize
                  minRows={2}
                  maxRows={10}
                  label='Question'
                  placeholder='Input question...'
                  error={errors.Title?.message}
                  required
                  disabled={isLoading}
                  {...field}
                />
              )}
            />

            <Controller
              name='Category'
              control={control}
              render={({ field }) => (
                <TagsInput
                  label='Question Categories'
                  placeholder='Type and press Enter to add categories'
                  description='Add multiple categories by typing and pressing Enter'
                  data={[]}
                  error={errors.Category?.message}
                  value={field.value}
                  onChange={field.onChange}
                  clearable
                  maxTags={10}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name='SkalaCategory'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Skala Category'
                  placeholder='Input skala category...'
                  error={errors.Title?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='Description'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Question Description'
                  placeholder='Input description...'
                  description='Optional description for the question'
                  error={errors.Description?.message}
                  disabled={isLoading}
                  {...field}
                />
              )}
            />

            <Controller
              name='Type'
              control={control}
              render={({ field }) => (
                <Select
                  label='Type of Question'
                  placeholder='Select type'
                  required
                  data={[
                    { value: 'Choose One', label: 'Choose One' },
                    { value: 'Range', label: 'Range' },
                    { value: 'Free-text', label: 'Free-text' }
                  ]}
                  error={errors.Type?.message}
                  disabled={isLoading}
                  {...field}
                />
              )}
            />
            {questionType === 'Range' && (
              <Stack gap='md'>
                <Group justify='space-between'>
                  <Text size='sm' fw={500}>
                    Range Items
                  </Text>
                  <RangeTemplateSelector onSelect={handleTemplateSelect} />
                </Group>
                <Table
                  verticalSpacing='sm'
                  horizontalSpacing='sm'
                  withTableBorder
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th w={40} /> {/* Column for delete button */}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {fields.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={3} style={{ textAlign: 'center' }}>
                          No range items added yet
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      fields.map((field, index) => (
                        <Table.Tr key={field.id}>
                          <Table.Td>
                            <Controller
                              name={`RangeItem.${index}.label`}
                              control={control}
                              render={({ field }) => (
                                <TextInput
                                  placeholder='e.g., Poor, Fair, Good'
                                  error={errors.RangeItem?.[index]?.label?.message}
                                  disabled={isLoading}
                                  {...field}
                                />
                              )}
                            />
                            <Controller
                              name={`RangeItem.${index}.msQuestionRangeItemID`}
                              control={control}
                              render={({ field }) => <input type='hidden' {...field} />}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Controller
                              name={`RangeItem.${index}.value`}
                              control={control}
                              render={({ field }) => (
                                <TextInput
                                  placeholder='e.g., 1, 2, 3'
                                  error={errors.RangeItem?.[index]?.value?.message}
                                  disabled={isLoading}
                                  {...field}
                                />
                              )}
                            />
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon
                              color='red'
                              onClick={() => remove(index)}
                              variant='light'
                              disabled={isLoading}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>

                <Button
                  variant='light'
                  color='#7B2183'
                  leftSection={<IconPlus size={16} />}
                  onClick={() => append({ label: '', value: '', sequence: fields.length + 1 })}
                  mt='xs'
                  disabled={isLoading}
                >
                  Add Range Item
                </Button>
              </Stack>
            )}

            <Controller
              name='MsGroupQuestionID'
              control={control}
              render={({ field }) => <input type='hidden' {...field} />}
            />

            {/* <Controller
              name='Sequence'
              control={control}
              render={({ field: { onChange, value, ...rest } }) => (
                <TextInput
                  label='Sequence'
                  placeholder='1'
                  description='Order of this question in the group'
                  type='number'
                  value={value?.toString() || '1'}
                  onChange={(e) => onChange(parseInt(e.target.value) || 1)}
                  disabled={isLoading}
                  {...rest}
                />
              )}
            /> */}

            {/* Hidden field for MsQuestionID */}
            <Controller
              name='MsQuestionID'
              control={control}
              render={({ field }) => <input type='hidden' {...field} />}
            />
          </Stack>

          <Group justify='flex-end' mt='xl'>
            <SecondaryButton text='Cancel' onClick={() => navigate(returnPath)} mr={10} />
            <PrimaryButton
              text='Update'
              type='submit'
              loading={isSubmitting || updateMutation.isPending}
            />
          </Group>
        </form>
      </div>
    </>
  );
};

export default EditQuestionPage;
